// Quadratic Equations lesson — Grade 9/10 Math
// Mirrors the photosynthesis schema in /app/frontend/src/lib/lessonData.js
// so it can plug into the same LessonStage + TeacherPanel components.

export const quadraticLesson = {
    sessionName: "Quadratic Equations",
    title: "Quadratic Equations",
    subtitle: "Curves, motion, area and the maths behind them",
    grade: "Grade 9–10 · Math",
    chapter: "Algebra · Quadratic Equations",
    subject: "Math",
    coverImage:
        "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&w=1600&q=85",
    coverAlt: "Mathematical equations written on a chalkboard",

    /* ---- physical slides ---- */
    slides: [
        // INTRO ----------------------------------------------------
        {
            id: "intro",
            section: "concept",
            mode: "intro",
            title: "Why do some paths curve?",
            subtitle: "From basketballs to fountains — the maths behind the curve",
            estimatedSeconds: 60,
            tags: ["overview"],
            fragments: [
                { caption: "Imagine a basketball shot, a fountain stream, a cricket ball in the air." },
                { caption: "Their paths are not straight lines — they curve. Today we unpack the maths behind that curve." },
            ],
        },

        // CONCEPT SLIDES ----------------------------------------------------
        {
            id: "what-is-quadratic",
            section: "concept",
            mode: "concept_teaching",
            title: "What is a quadratic equation?",
            estimatedSeconds: 90,
            tags: ["definition"],
            fragments: [
                { bullet: "Highest power of x is 2", caption: "A quadratic equation is one where the highest power of the variable is 2." },
                { bullet: "Standard form: ax² + bx + c = 0", caption: "The standard form is ax squared plus bx plus c equals zero." },
                { bullet: "a ≠ 0", caption: "If a = 0, the x² term disappears and it becomes linear, not quadratic." },
                { bullet: "Examples: 2x² + 5x + 3 = 0, x² - 9 = 0", caption: "These are all quadratic. Note: x² - 9 = 0 is shorthand for x² + 0x - 9 = 0." },
            ],
        },
        {
            id: "basketball-video",
            section: "concept",
            mode: "video_teaching",
            title: "Watch: Basketball arc",
            estimatedSeconds: 45,
            tags: ["video", "real-life"],
            videoUrl:
                "https://asset.sparkl.ac/pb/poc-folder/aH5ePnO6wxJUmj2guTLByleoit6csDNBy1MhzZtv.mp4",
            videoPoster:
                "https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=1600&q=80",
            fragments: [
                {
                    caption:
                        "Watch how a basketball traces a perfect parabola from the player's hand to the hoop.",
                },
                {
                    caption:
                        "Notice the rise, the peak, and the fall — that curve is exactly what a quadratic equation models.",
                },
            ],
        },
        {
            id: "shape-of-x2",
            section: "concept",
            mode: "diagram_teaching",
            title: "The shape of x² — a parabola",
            estimatedSeconds: 90,
            tags: ["graph", "parabola"],
            fragments: [
                { bullet: "Plot some values", caption: "When x is -2 or +2, y becomes 4. When x is -3 or +3, y becomes 9." },
                { bullet: "Join them smoothly", caption: "The points form a U-shape." },
                { bullet: "This U-shape is called a parabola", caption: "The lowest point is called the vertex. For y = x², the vertex sits at the origin." },
            ],
        },
        {
            id: "why-curves-open",
            section: "concept",
            mode: "concept_teaching",
            title: "Why does the curve open upward or downward?",
            estimatedSeconds: 75,
            tags: ["coefficient"],
            fragments: [
                { bullet: "The coefficient of x² controls the opening", caption: "It's the value of 'a' in y = ax²." },
                { bullet: "a > 0 → opens upward", caption: "Positive a, U-shape." },
                { bullet: "a < 0 → opens downward", caption: "Negative a, the U flips upside down." },
                { bullet: "Bigger |a| → narrower; smaller |a| → wider", caption: "The magnitude controls how tight or wide the curve is." },
            ],
        },
        {
            id: "roots-intercepts",
            section: "concept",
            mode: "diagram_teaching",
            title: "Roots: where the graph meets the x-axis",
            estimatedSeconds: 75,
            tags: ["roots"],
            fragments: [
                { bullet: "Solving = finding x-values where graph touches x-axis", caption: "When we solve a quadratic, we find the x-values where y = 0." },
                { bullet: "These x-values are called roots or zeroes", caption: "Also known as x-intercepts." },
                { bullet: "Example: y = x² - 5x + 6 meets x-axis at x = 2 and x = 3", caption: "So the equation x² - 5x + 6 = 0 has roots 2 and 3." },
            ],
        },
        {
            id: "factor-form-bridge",
            section: "concept",
            mode: "concept_teaching",
            title: "From roots to factor form",
            estimatedSeconds: 70,
            tags: ["factoring"],
            fragments: [
                { bullet: "If roots are 2 and 3 → factors are (x-2) and (x-3)", caption: "Each root gives a factor: x = 2 means x - 2 = 0." },
                { bullet: "(x - 2)(x - 3) = 0", caption: "Multiply the factors and set to zero." },
                { bullet: "Expand → x² - 5x + 6 = 0", caption: "Back to the standard form we started with." },
            ],
        },
        {
            id: "factorisation-method",
            section: "concept",
            mode: "diagram_teaching",
            title: "Solving by factorisation",
            estimatedSeconds: 100,
            tags: ["method"],
            fragments: [
                { bullet: "Example: x² + 5x + 6 = 0", caption: "Find two numbers whose product is 6 and sum is 5." },
                { bullet: "Those numbers are 2 and 3", caption: "2 × 3 = 6 and 2 + 3 = 5 ✓" },
                { bullet: "Split: x² + 2x + 3x + 6 = 0", caption: "Use the two numbers to split the middle term." },
                { bullet: "Group + factor: x(x+2) + 3(x+2) = 0 → (x+2)(x+3) = 0", caption: "Take out the common factor." },
                { bullet: "Roots: x = -2, x = -3", caption: "Set each factor to zero." },
            ],
        },
        {
            id: "completing-square",
            section: "concept",
            mode: "diagram_teaching",
            title: "Completing the square",
            estimatedSeconds: 110,
            tags: ["method"],
            fragments: [
                { bullet: "Rewrite x² + 6x as a perfect square", caption: "Half of 6 is 3 — and 3² = 9." },
                { bullet: "Add 9 to complete: x² + 6x + 9 = (x + 3)²", caption: "We've completed the square." },
                { bullet: "Solve x² + 6x + 5 = 0", caption: "Step 1 — move 5 to the right: x² + 6x = -5." },
                { bullet: "Step 2 — add 9 to both sides", caption: "x² + 6x + 9 = -5 + 9 → (x + 3)² = 4." },
                { bullet: "Step 3 — square-root both sides", caption: "x + 3 = ±2, so x = -1 or x = -5." },
            ],
        },
        {
            id: "quadratic-formula",
            section: "concept",
            mode: "concept_teaching",
            title: "The quadratic formula",
            estimatedSeconds: 100,
            tags: ["formula"],
            fragments: [
                { bullet: "Works for every quadratic equation", caption: "Even ones that don't factor nicely." },
                { bullet: "x = [-b ± √(b² - 4ac)] / 2a", caption: "Once you know a, b, c — you can find both roots directly." },
                { bullet: "Example: 2x² + 3x - 2 = 0 → a=2, b=3, c=-2", caption: "Plug into the formula." },
                { bullet: "x = [-3 ± √(9 + 16)] / 4 = [-3 ± 5] / 4", caption: "Simplify under the square root first." },
                { bullet: "x = 1/2 or x = -2", caption: "Two roots from one formula." },
            ],
        },
        {
            id: "discriminant",
            section: "concept",
            mode: "concept_teaching",
            title: "Discriminant — the root detector",
            estimatedSeconds: 90,
            tags: ["discriminant"],
            fragments: [
                { bullet: "D = b² - 4ac", caption: "The expression inside the square root in the quadratic formula." },
                { bullet: "D > 0 → two distinct real roots", caption: "Graph cuts the x-axis at two different points." },
                { bullet: "D = 0 → two equal real roots", caption: "Graph just touches the x-axis at one point." },
                { bullet: "D < 0 → no real roots", caption: "Graph doesn't touch the x-axis at all." },
            ],
        },
        {
            id: "max-min-vertex",
            section: "concept",
            mode: "diagram_teaching",
            title: "Maximum and minimum values",
            estimatedSeconds: 70,
            tags: ["vertex"],
            fragments: [
                { bullet: "The vertex is the turning point of a parabola", caption: "It's the highest or lowest point of the curve." },
                { bullet: "Upward parabola → vertex gives MINIMUM", caption: "Lowest point of the U." },
                { bullet: "Downward parabola → vertex gives MAXIMUM", caption: "Highest point of the flipped U." },
            ],
        },

        // CONTAINER SLIDES ----------------------------------------------------
        {
            id: "mcq-display",
            section: "concept-check",
            mode: "mcq_display",
            title: "Concept Check",
            estimatedSeconds: 0,
            tags: ["mcq"],
            fragments: [{ caption: "Concept check questions for Quadratic Equations." }],
        },
        {
            id: "subjective-display",
            section: "practice",
            mode: "subjective_display",
            title: "Practice / Solved Example",
            estimatedSeconds: 0,
            tags: ["practice"],
            fragments: [{ caption: "Subjective practice for Quadratic Equations." }],
        },
        {
            id: "drag-drop",
            section: "activity",
            mode: "drag_drop",
            title: "Quadratic or not? Drag-and-drop",
            estimatedSeconds: 0,
            tags: ["activity"],
            fragments: [{ caption: "Sort each equation into Quadratic or Not Quadratic." }],
        },
        {
            id: "blank-slide",
            section: "tools",
            mode: "blank_slide",
            title: "Blank teaching slide",
            estimatedSeconds: 0,
            tags: ["blank"],
            fragments: [{ caption: "Free-form board for live explanation." }],
        },
        {
            id: "recap",
            section: "recap",
            mode: "recap",
            title: "Big recap",
            estimatedSeconds: 60,
            tags: ["recap"],
            fragments: [
                { caption: "A quadratic equation has degree 2 and graphs as a parabola." },
                { caption: "Roots are where the graph meets the x-axis." },
                { caption: "Three solving methods: factorisation, completing the square, quadratic formula." },
                { caption: "The discriminant tells us the nature of the roots." },
            ],
        },
    ],

    /* ---- Sections (mirror photosynthesis schema: slideIds on concept/activity/recap) ---- */
    sections: [
        {
            id: "concept",
            title: "Concept",
            description: "Auto-flows unless paused",
            slideIds: [
                "intro",
                "what-is-quadratic",
                "basketball-video",
                "shape-of-x2",
                "why-curves-open",
                "roots-intercepts",
                "factor-form-bridge",
                "factorisation-method",
                "completing-square",
                "quadratic-formula",
                "discriminant",
                "max-min-vertex",
            ],
        },
        { id: "concept-check", title: "Concept Check", description: "5 MCQs" },
        {
            id: "practice",
            title: "Practice",
            description: "6 subjective questions",
        },
        {
            id: "activity",
            title: "Activity",
            description: "Drag & Drop",
            slideIds: ["drag-drop"],
        },
        {
            id: "recap",
            title: "Recap",
            description: "Key takeaways",
            slideIds: ["recap"],
        },
    ],

    /* ---- 5 concept-check MCQs ---- */
    mcqs: [
        {
            id: "m1",
            question: "Which of the following is a quadratic equation?",
            options: ["3x + 5 = 0", "x² - 4x + 4 = 0", "x³ + x + 1 = 0", "1/x + 2 = 0"],
            correctIndex: 1,
            difficulty: "easy",
            tag: "definition",
            estimatedSeconds: 30,
            relatedConceptId: "what-is-quadratic",
            explanation:
                "A quadratic equation has highest power 2. x² - 4x + 4 = 0 fits the form ax² + bx + c = 0.",
        },
        {
            id: "m2",
            question: "The roots of x² - 5x + 6 = 0 are:",
            options: ["2 and 3", "-2 and -3", "1 and 6", "-1 and -6"],
            correctIndex: 0,
            difficulty: "easy",
            tag: "roots",
            estimatedSeconds: 30,
            relatedConceptId: "factorisation-method",
            explanation:
                "x² - 5x + 6 factors as (x - 2)(x - 3), so the roots are 2 and 3.",
        },
        {
            id: "m3",
            question:
                "If the discriminant of a quadratic equation is zero, then the equation has:",
            options: [
                "Two distinct real roots",
                "Two equal real roots",
                "No real roots",
                "Only one complex root",
            ],
            correctIndex: 1,
            difficulty: "medium",
            tag: "discriminant",
            estimatedSeconds: 40,
            relatedConceptId: "discriminant",
            explanation:
                "When D = 0, the parabola just touches the x-axis at one point — two equal real roots.",
        },
        {
            id: "m4",
            question: "For y = -2x², the parabola:",
            options: [
                "Opens upward",
                "Opens downward",
                "Is a straight line",
                "Does not touch the y-axis",
            ],
            correctIndex: 1,
            difficulty: "easy",
            tag: "coefficient",
            estimatedSeconds: 25,
            relatedConceptId: "why-curves-open",
            explanation:
                "Since the coefficient of x² is negative, the parabola opens downward.",
        },
        {
            id: "m5",
            question:
                "Which method works for every quadratic equation, even ones that don't factor nicely?",
            options: [
                "Factorisation",
                "Guess and check",
                "The quadratic formula",
                "Drawing the graph",
            ],
            correctIndex: 2,
            difficulty: "medium",
            tag: "formula",
            estimatedSeconds: 30,
            relatedConceptId: "quadratic-formula",
            explanation:
                "The quadratic formula gives roots for any ax² + bx + c = 0 (a ≠ 0).",
        },
    ],

    /* ---- 6 subjective questions ---- */
    subjectives: [
        {
            id: "s1",
            question: "Solve x² - 8x + 15 = 0 by factorisation.",
            difficulty: "easy",
            tag: "factorisation",
            estimatedSeconds: 120,
            relatedConceptId: "factorisation-method",
            hint: "Find two numbers whose product is 15 and sum is -8.",
            expectedPoints: [
                "Numbers: -3 and -5 (product 15, sum -8).",
                "Factor: (x - 3)(x - 5) = 0.",
                "Roots: x = 3 or x = 5.",
            ],
            commonMistake:
                "Forgetting to include the negative sign when both factors are negative.",
            steps: [
                {
                    title: "Find the two numbers",
                    body: "Product 15, sum -8 → -3 and -5.",
                },
                {
                    title: "Write the factor form",
                    body: "(x - 3)(x - 5) = 0.",
                },
                {
                    title: "Set each factor to zero",
                    body: "x = 3 or x = 5.",
                },
            ],
        },
        {
            id: "s2",
            question: "Use the quadratic formula to solve 2x² + 7x + 3 = 0.",
            difficulty: "medium",
            tag: "formula",
            estimatedSeconds: 150,
            relatedConceptId: "quadratic-formula",
            hint: "Identify a = 2, b = 7, c = 3 and substitute carefully.",
            expectedPoints: [
                "a = 2, b = 7, c = 3.",
                "Discriminant D = 49 - 24 = 25.",
                "x = (-7 ± 5)/4 → x = -1/2 or x = -3.",
            ],
            commonMistake:
                "Calculating the discriminant as 49 + 24 instead of 49 - 24.",
            steps: [
                {
                    title: "Identify a, b, c",
                    body: "a = 2, b = 7, c = 3.",
                },
                {
                    title: "Compute the discriminant",
                    body: "D = b² - 4ac = 49 - 24 = 25.",
                },
                {
                    title: "Plug into the formula",
                    body: "x = (-7 ± 5)/4 → x = -1/2 or x = -3.",
                },
            ],
        },
        {
            id: "s3",
            question:
                "Find the nature of roots of 3x² - 2x + 5 = 0 using the discriminant.",
            difficulty: "medium",
            tag: "discriminant",
            estimatedSeconds: 90,
            relatedConceptId: "discriminant",
            hint: "Compute D = b² - 4ac and check its sign.",
            expectedPoints: [
                "D = (-2)² - 4(3)(5) = 4 - 60 = -56.",
                "Since D < 0, no real roots exist.",
                "The parabola never touches the x-axis.",
            ],
            commonMistake: "Squaring -2 and getting -4 instead of +4.",
            steps: [
                {
                    title: "Compute D",
                    body: "D = (-2)² - 4(3)(5) = 4 - 60 = -56.",
                },
                {
                    title: "Interpret the sign",
                    body: "D < 0 → no real roots.",
                },
            ],
        },
        {
            id: "s4",
            question:
                "A garden has length (x + 6) m and width (x + 2) m. If its area is 48 m², find x.",
            difficulty: "hard",
            tag: "word-problem",
            estimatedSeconds: 180,
            relatedConceptId: "factorisation-method",
            hint: "Form the area equation, expand, simplify to standard form, then solve.",
            expectedPoints: [
                "(x+6)(x+2) = 48 → x² + 8x + 12 = 48.",
                "Standard form: x² + 8x - 36 = 0.",
                "Factor / formula → x = 3 (positive root, since length must be positive).",
            ],
            commonMistake: "Accepting x = -12 even though length must be positive.",
            steps: [
                {
                    title: "Form the equation",
                    body: "(x+6)(x+2) = 48.",
                },
                {
                    title: "Expand & simplify",
                    body: "x² + 8x + 12 = 48 → x² + 8x - 36 = 0.",
                },
                {
                    title: "Solve and pick the valid root",
                    body: "Roots ≈ x = 3 or x ≈ -11. Take x = 3.",
                },
            ],
        },
        {
            id: "s5",
            question:
                "Explain how the roots of a quadratic equation relate to the graph of y = ax² + bx + c.",
            difficulty: "medium",
            tag: "graph",
            estimatedSeconds: 120,
            relatedConceptId: "roots-intercepts",
            hint: "Where on the graph is y = 0?",
            expectedPoints: [
                "Roots are x-values where the graph crosses the x-axis (x-intercepts).",
                "Two intercepts → two distinct real roots.",
                "One touch → equal real roots; no intercepts → no real roots.",
            ],
            commonMistake:
                "Confusing roots with the y-intercept (which is just c).",
            steps: [
                {
                    title: "Set y = 0",
                    body: "Roots are x-values where y = 0 — i.e. x-intercepts.",
                },
                {
                    title: "Connect to the discriminant",
                    body: "Number of intercepts = number of real roots.",
                },
            ],
        },
        {
            id: "s6",
            question: "Solve 2x² + 5x + 2 = 0 using factorisation.",
            difficulty: "medium",
            tag: "factorisation",
            estimatedSeconds: 150,
            relatedConceptId: "factorisation-method",
            hint: "Look for factor pairs of 4 (= 2 × 2) that sum to 5.",
            expectedPoints: [
                "Split: 2x² + 4x + x + 2 = 0.",
                "Group: 2x(x + 2) + 1(x + 2) = 0 → (2x + 1)(x + 2) = 0.",
                "Roots: x = -1/2 or x = -2.",
            ],
            commonMistake: "Forgetting to multiply 'a' with 'c' when finding the split.",
            steps: [
                {
                    title: "Multiply a × c",
                    body: "2 × 2 = 4. Find two numbers with product 4 and sum 5 → 4 and 1.",
                },
                {
                    title: "Split the middle term",
                    body: "2x² + 4x + x + 2 = 0.",
                },
                {
                    title: "Group + factor",
                    body: "(2x + 1)(x + 2) = 0 → x = -1/2 or x = -2.",
                },
            ],
        },
    ],

    /* ---- Drag-drop activity (single object, matches photosynthesis schema) ---- */
    activity: {
        id: "drag-drop-1",
        title: "Quadratic or Not Quadratic?",
        objective: "Sort each equation by whether it is a quadratic.",
        instruction:
            "Drag each equation into the correct bucket — is it quadratic, or not?",
        difficulty: "easy",
        duration: 180,
        items: [
            { id: "i1", label: "x² + 4x + 4 = 0", correct: "quadratic" },
            { id: "i2", label: "3x + 7 = 0", correct: "not-quadratic" },
            { id: "i3", label: "x³ - x + 1 = 0", correct: "not-quadratic" },
            { id: "i4", label: "5x² - 2 = 0", correct: "quadratic" },
            { id: "i5", label: "x² = 9", correct: "quadratic" },
            { id: "i6", label: "1/x + 2 = 0", correct: "not-quadratic" },
            // distractors
            { id: "i7", label: "2x = 8", correct: null },
            { id: "i8", label: "y = mx + c", correct: null },
        ],
        categories: [
            { id: "quadratic", label: "Quadratic" },
            { id: "not-quadratic", label: "Not quadratic" },
        ],
    },

    /* ---- Blank slate variants ---- */
    blankSlideTypes: [
        { id: "white", label: "White board" },
        { id: "lined", label: "Lined" },
        { id: "grid", label: "Grid" },
        { id: "diagram", label: "Diagram" },
    ],

    /* ---- real-life examples ---- */
    realLifeExamples: [
        {
            id: "e1",
            text: "A basketball shot follows a parabola.",
            headline: "Basketball arc",
            description:
                "When a player shoots, the ball follows a parabolic path. The maths predicting whether it goes in is a quadratic equation in height vs time.",
            image:
                "https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=1600&q=80",
        },
        {
            id: "e2",
            text: "Fountain streams are parabolas.",
            headline: "Fountain streams",
            description:
                "Water leaving a fountain nozzle traces a parabola. Engineers solve quadratic equations to design the height and spread of each jet.",
            image:
                "https://images.unsplash.com/photo-1530856935471-2389e1d11b9d?auto=format&fit=crop&w=1600&q=80",
        },
        {
            id: "e3",
            text: "Bridge cables hang in a parabola-like curve.",
            headline: "Suspension bridge cables",
            description:
                "The main cables of a suspension bridge approximate a parabola when loaded with a uniform deck — engineers use quadratic models to size them.",
            image:
                "https://images.unsplash.com/photo-1465311354284-39e6d0c8d09e?auto=format&fit=crop&w=1600&q=80",
        },
        {
            id: "e4",
            text: "Garden area problems lead to quadratics.",
            headline: "Garden area",
            description:
                "A rectangular plot's area = length × width. If both dimensions depend on x, multiplying them creates an x² term — a quadratic equation.",
            image:
                "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=1600&q=80",
        },
    ],

    /* ---- teacher-help library ---- */
    teacherHelp: {
        "what-is-quadratic": {
            simple:
                "A quadratic equation has the form ax² + bx + c = 0 with a ≠ 0.",
            probing: "What happens if a = 0?",
            misconception: "Students may forget that a cannot be zero.",
            analogy:
                "It's like a recipe with a fixed first ingredient — without it, the dish changes entirely.",
        },
        "quadratic-formula": {
            simple:
                "The quadratic formula gives both roots directly from a, b, c.",
            probing: "Why is there a ± sign?",
            misconception: "Forgetting the ± and reporting only one root.",
            analogy: "It's a master key that opens any quadratic lock.",
        },
        discriminant: {
            simple: "D = b² - 4ac. Its sign tells you how many real roots exist.",
            probing: "What does it mean for a parabola to never touch the x-axis?",
            misconception: "Computing D as b² + 4ac (sign error).",
            analogy:
                "Like a weather forecast — predicts what kind of roots you'll find.",
        },
    },

    /* ---- AI recommendations, keyed by slide id ---- */
    aiRecommendations: {
        intro: [
            { id: "rec-intro-1", text: "Ask students to name 3 places they've seen curved paths.", action: "annotate" },
        ],
        "what-is-quadratic": [
            { id: "rec-what-1", text: "Try MCQ Q1 — 'Which of these is quadratic?'", kind: "show-mcq", target: "m1" },
        ],
        "shape-of-x2": [
            { id: "rec-shape-1", text: "Show 'Basketball arc' real-life example.", kind: "show-example", target: "e1" },
        ],
        "why-curves-open": [
            { id: "rec-open-1", text: "Quick MCQ — 'For y = -2x², the parabola...'", kind: "show-mcq", target: "m4" },
        ],
        "roots-intercepts": [
            { id: "rec-roots-1", text: "Try MCQ Q2 — roots of x² - 5x + 6.", kind: "show-mcq", target: "m2" },
        ],
        "factorisation-method": [
            { id: "rec-fac-1", text: "Solved example: Solve x² - 8x + 15 = 0.", kind: "show-subjective", target: "s1" },
            { id: "rec-fac-2", text: "Start the drag-drop activity.", kind: "start-activity" },
        ],
        "quadratic-formula": [
            { id: "rec-qf-1", text: "Solved example: 2x² + 7x + 3 = 0.", kind: "show-subjective", target: "s2" },
        ],
        discriminant: [
            { id: "rec-d-1", text: "Try MCQ Q3 — when D = 0.", kind: "show-mcq", target: "m3" },
            { id: "rec-d-2", text: "Solved example: nature of roots of 3x² - 2x + 5 = 0.", kind: "show-subjective", target: "s3" },
        ],
        recap: [
            { id: "rec-rcp-1", text: "Quickly cycle through all 4 real-life examples.", action: "annotate" },
        ],
    },
};

export default quadraticLesson;
